import { yupResolver } from '@hookform/resolvers/yup';
import { HelpOutline } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
	Box,
	Button,
	Dialog,
	DialogContent,
	FormControl,
	LinearProgress,
	Link,
	Stack,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import Collapse from '@mui/material/Collapse';
import Tooltip from '@mui/material/Tooltip';
import dayjs from 'dayjs';
import { uniq } from 'lodash';
import * as React from 'react';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { SelectElement } from 'react-hook-form-mui';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { useAuthContext } from '../../../../../../context/auth';
import { useConfigContext } from '../../../../../../context/config';
import colors from '../../../../../../util/palette';
import {
	DATE_FIELD_VALIDATIONS,
	DATE_PRECISION_PLACEHOLDER,
	FORM_MESSAGE,
} from '../../../../../fastForm/data/constants';
import { DateField } from '../../../../../fastForm/fields';
import { dateConversion } from '../../../../../fastForm/utils';
import FieldLabel from '../../../../../fields/FieldLabel';
import ResourcesAPI, {
	DeployResourceRequest,
	ResourceType,
} from '../../../api/resources';
import {
	DATE_COMPLIANCE_BULK_INFO_MESSAGE,
	DATE_COMPLIANCE_BULK_UPLOAD,
	DATE_COMPLIANCE_CANCEL_BUTTON,
	DATE_COMPLIANCE_CREATE_MODAL_TOOLTIP,
	DATE_COMPLIANCE_EFFECTIVE_DATE_FIELD,
	DATE_COMPLIANCE_FINISHED_BUTTON,
	DATE_COMPLIANCE_PRODUCT_LABEL,
	DATE_COMPLIANCE_SINGLE_FILE_INFO_MESSAGE,
	DATE_COMPLIANCE_SINGLE_FILE_UPLOAD,
	DATE_COMPLIANCE_SUBMIT_BUTTON,
	DATE_COMPLIANCE_TIMESTAMPS_PRODUCT_LABEL,
	DATE_COMPLIANCE_UPLOADING_MESSAGE,
	MODULAR_CONFIG_UPLOAD_DIALOG_WIDTH,
} from '../../../data/constants';
import { handleModalClose } from '../../../utils/handleModalClose';
import { RESOURCES_KEY } from '../ResourceGroups';
import DeployResponseSummary from './DeployResponseSummary';
import DropzoneFileUploadField from './DropzoneFileUploadField';
import ResourceTypeRadioGroup from './ResourceTypeRadioGroup';

const DATE_PICKER_PRECISION = 'hour';

export interface ResourcesFormData {
	file: File;
	product: string;
	effectiveDate: string;
	resourceType: ResourceType;
}

export enum UploadType {
	BULK = 'bulk',
	SINGLE = 'single',
}

interface DeployResourcesModalProps {
	handleClose: () => void;
	open: boolean;
	product?: string;
	isProductEditable: boolean;
	effectiveTimestamp?: number;
	resourceType?: ResourceType;
}

const acceptedFilesByResourceType = {
	[ResourceType.TABLES]: {
		'application/zip': ['.zip'],
		'text/*': ['.csv'],
	},
	[ResourceType.STATIC_DOCUMENTS]: {
		'application/*': ['.zip', '.pdf'],
	},
	[ResourceType.DOCUMENT_TEMPLATES]: {
		'application/*': ['.zip', '.liquid'],
	},
	[ResourceType.FORMULA_TEMPLATES]: {
		'application/*': ['.zip', '.liquid'],
	},
};

const DeployResourcesModal = (props: DeployResourcesModalProps) => {
	const { tenantHostname } = useParams();
	const {
		open,
		handleClose,
		resourceType,
		product,
		effectiveTimestamp,
		isProductEditable,
	} = props;

	const authData = useAuthContext();
	const config = useConfigContext();
	const productConfigs = config?.productConfigs;
	const timezone = authData?.auth?.timezone;
	const authToken = authData?.tenantAuthorizationData?.authorizationToken;
	const tenantId = authData?.tenantAuthorizationData?.tenantId;
	const [successState, setSuccessState] = React.useState<boolean>(false);
	const [uploadType, setUploadType] = React.useState<UploadType>(
		UploadType.BULK,
	);

	const {
		mutateAsync: addResource,
		isLoading,
		data,
	} = ResourcesAPI.useAddResourceMutation(
		[
			RESOURCES_KEY,
			tenantId || tenantHostname,
			product,
			effectiveTimestamp?.toString(10),
		],
		authToken,
		tenantHostname,
	);

	const productOptions = productConfigs
		? uniq(
				productConfigs
					.filter((config) => config.name !== 'global')
					.map((product) => {
						return {
							id: product.name,
							label: product.name,
						};
					}),
		  )
		: [];

	const defaultValues = {
		product: product || '',
		file: null as File,
		effectiveDate: effectiveTimestamp
			? dateConversion.dayJsObjectToDateFormString(
					dayjs(effectiveTimestamp),
					DATE_PICKER_PRECISION,
			  )
			: '',
		resourceType: resourceType || ResourceType.TABLES,
	};
	const {
		handleSubmit,
		control,
		reset,
		watch,
		setValue,
		setError,
		clearErrors,
	} = useForm<ResourcesFormData>({
		resolver: yupResolver(
			yup.object().shape({
				product: yup.string().required(FORM_MESSAGE.required),
				effectiveDate: yup
					.string()
					.required(FORM_MESSAGE.required)
					.test(
						'is-valid-date',
						DATE_FIELD_VALIDATIONS.INVALID_FORMAT_ERROR_MESSAGE +
							DATE_PRECISION_PLACEHOLDER[DATE_PICKER_PRECISION],
						(value) => {
							const date = dateConversion.dateFormStringToDayJs(
								value,
								DATE_PICKER_PRECISION,
							);
							return date?.isValid();
						},
					),
				file: yup.mixed().test({
					test: (value) => {
						if (!value || !value?.name) {
							return false;
						} else {
							return true;
						}
					},
					message: FORM_MESSAGE.required,
				}),
			}),
		),
		mode: 'onChange',
		defaultValues: defaultValues,
	});

	useEffect(() => {
		if (resourceType) {
			setUploadType(UploadType.SINGLE);
			setValue('resourceType', resourceType);
		}
	}, [resourceType, setValue]);

	const onCancel = () => {
		reset(defaultValues);
		if (!resourceType) {
			setUploadType(UploadType.BULK);
		} else {
			setUploadType(UploadType.SINGLE);
		}
		handleClose();
	};

	const currentResourceType = watch('resourceType');
	const selectedProduct = watch('product');

	useEffect(() => {
		setValue('file', null);
		clearErrors('file');
	}, [uploadType, setValue, clearErrors]);
	useEffect(() => {
		clearErrors('file');
		setValue('file', null);
	}, [currentResourceType, setValue, clearErrors]);
	useEffect(() => {
		setValue('product', product);
	}, [product, setValue]);

	const onSubmit: SubmitHandler<ResourcesFormData> = async (data) => {
		const request = {} as DeployResourceRequest;
		request.file = data.file;

		request.productName = data.product;

		if (uploadType === UploadType.SINGLE) {
			request.resourcesType = data.resourceType;
		}
		if (data.effectiveDate) {
			request.effectiveTimestamp = parseInt(
				dateConversion.convertDateStringToZonedTimestamp(
					data.effectiveDate,
					DATE_PICKER_PRECISION,
					timezone,
				),
			);
		}
		await addResource(request);
		setSuccessState(true);
	};

	const onFinish = () => {
		handleClose();
		setSuccessState(false);
		reset(defaultValues);
		if (!resourceType) {
			setUploadType(UploadType.BULK);
		} else {
			setUploadType(UploadType.SINGLE);
		}
	};

	const showFormInputs = !isLoading && !successState;

	const toolTipContent = (
		<Typography>{DATE_COMPLIANCE_CREATE_MODAL_TOOLTIP()}</Typography>
	);

	return (
		<Dialog
			fullWidth
			open={open}
			aria-labelledby='deploy-resources-modal'
			onClose={(e, r) => handleModalClose(r, handleClose)}
		>
			{!successState && !isLoading && (
				<Box
					sx={{
						position: 'absolute',
						display: 'flex',
						width: '100%',
						justifyContent: 'flex-end',
						padding: (theme) => theme.spacing(2),
					}}
				>
					<Tooltip
						title={toolTipContent}
						componentsProps={{
							tooltip: {
								sx: {
									maxWidth: '700px',
									backgroundColor: colors.primary.dark,
									padding: (theme) => theme.spacing(2),
								},
							},
							arrow: { sx: { color: colors.primary.main } },
						}}
						arrow
						placement='top'
					>
						<HelpOutline sx={{ color: colors.black.lighter }} />
					</Tooltip>
				</Box>
			)}

			<DialogContent sx={{ width: MODULAR_CONFIG_UPLOAD_DIALOG_WIDTH }}>
				<Box
					onSubmit={handleSubmit(onSubmit)}
					component='form'
					sx={{ pt: 1, pb: 2 }}
				>
					<Collapse in={isLoading}>
						<Stack sx={{ width: '100%' }} spacing={2}>
							{DATE_COMPLIANCE_UPLOADING_MESSAGE()}
							<LinearProgress />
						</Stack>
					</Collapse>
					<Collapse in={successState}>
						<DeployResponseSummary response={data || []} />
					</Collapse>
					<Collapse in={showFormInputs}>
						<Stack
							alignItems='flex-start'
							direction='column'
							spacing={2}
							mt={1}
						>
							<FormControl fullWidth={true}>
								<FieldLabel
									title={DATE_COMPLIANCE_TIMESTAMPS_PRODUCT_LABEL()}
									required={true}
									htmlFor='product'
								/>
								<SelectElement
									disabled={!isProductEditable}
									name='product'
									label={
										selectedProduct ? null : DATE_COMPLIANCE_PRODUCT_LABEL()
									}
									options={productOptions}
									control={control}
									fullWidth
									variant='outlined'
									size='medium'
								/>
							</FormControl>

							<DateField<ResourcesFormData>
								name='effectiveDate'
								title={DATE_COMPLIANCE_EFFECTIVE_DATE_FIELD()}
								control={control}
								disabled={isLoading || !!effectiveTimestamp}
								defaultToMidnight={true}
								precision={DATE_PICKER_PRECISION}
								authData={authData}
								order={0}
								values={[]}
								disableTimePicker={false}
								showCalendar
								type='date'
								variant='outlined'
							/>
							<Tabs
								value={uploadType}
								onChange={(e, value) => setUploadType(value)}
								textColor='secondary'
								indicatorColor='secondary'
								aria-label='primary tabs upload'
							>
								<Tab
									value={UploadType.BULK}
									label={DATE_COMPLIANCE_BULK_UPLOAD()}
								/>
								<Tab
									value={UploadType.SINGLE}
									label={DATE_COMPLIANCE_SINGLE_FILE_UPLOAD()}
								/>
							</Tabs>

							{uploadType === UploadType.SINGLE && (
								<Stack width='100%' mt={3} mb={4}>
									<Stack
										direction='row'
										spacing={1}
										alignContent='center'
										color='#777777'
									>
										<InfoOutlinedIcon fontSize='small' />
										<Typography variant='body2' align='left'>
											{DATE_COMPLIANCE_SINGLE_FILE_INFO_MESSAGE()}
										</Typography>
									</Stack>
									<Stack width='100%' direction='row' spacing={2} mt={2}>
										<ResourceTypeRadioGroup control={control} />
									</Stack>
									<DropzoneFileUploadField
										onFileUpload={(f) => {
											setValue('file', f);
											clearErrors();
										}}
										control={control}
										onError={(e) =>
											setError('file', {
												message: e.message,
											})
										}
										onRejection={(rejectedFiles) =>
											setError('file', {
												message: rejectedFiles[0].errors
													.map((e) => e.message)
													.join('. '),
											})
										}
										disabled={isLoading}
										accept={acceptedFilesByResourceType[currentResourceType]}
									/>
								</Stack>
							)}
							{uploadType === UploadType.BULK && (
								<Stack width='100%' mt={3} mb={4} spacing={2}>
									<Link
										sx={{
											display: 'flex',
											flexDirection: 'row',
											alignContent: 'center',
											textDecoration: 'none',
											color: '#777777',
										}}
										target='_blank'
										href='https://docs.socotra.com/production/index.html'
									>
										<InfoOutlinedIcon
											fontSize='small'
											sx={{ marginRight: '5px' }}
										/>
										<Typography variant='body2' align='left'>
											{DATE_COMPLIANCE_BULK_INFO_MESSAGE()}
										</Typography>
									</Link>

									<DropzoneFileUploadField
										onFileUpload={(f) => {
											setValue('file', f);
											clearErrors();
										}}
										control={control}
										onError={(e) =>
											setError('file', {
												message: e.message,
											})
										}
										onRejection={(rejectedFiles) =>
											setError('file', {
												message: rejectedFiles[0].errors
													.map((e) => e.message)
													.join('. '),
											})
										}
										disabled={isLoading}
										accept={{
											'application/zip': ['.zip'],
										}}
									/>
								</Stack>
							)}
						</Stack>
					</Collapse>

					<Stack
						direction='row'
						spacing={2}
						justifyContent='flex-end'
						sx={{ mt: 2 }}
					>
						{!successState && (
							<Button onClick={onCancel} color='inherit' disabled={isLoading}>
								{DATE_COMPLIANCE_CANCEL_BUTTON()}
							</Button>
						)}
						{!successState && (
							<Button
								type='submit'
								variant='contained'
								color='secondary'
								disabled={isLoading}
							>
								{DATE_COMPLIANCE_SUBMIT_BUTTON()}
							</Button>
						)}
						{successState && (
							<Button onClick={onFinish} color='inherit' disabled={isLoading}>
								{DATE_COMPLIANCE_FINISHED_BUTTON()}
							</Button>
						)}
					</Stack>
				</Box>
			</DialogContent>
		</Dialog>
	);
};

export default DeployResourcesModal;
